import * as React from 'react';
import * as Swipeable from 'react-swipeable';
import { throttle } from 'lodash';
import Arrow from '../Arrow/Arrow';

import './Carousel.scss';
interface IResponsive {
  [key: number]: {
    items: number;
    showArrows: boolean;
    singleitemPercentage?: number;
    mobileException?: boolean;
  };
}

interface ICarouselProps {
  children: any;
  responsive: IResponsive;
}

interface ICarouselState {
  currentIndex: number;
  translateValue: number;
  enableRightArrow: boolean;
  enableLeftArrow: boolean;
  itemsToShow: number;
  showArrows: boolean;
}

export class Carousel extends React.Component<ICarouselProps, ICarouselState> {
  constructor(props: any) {
    super(props);
    this.state = {
      currentIndex: 0,
      translateValue: 0,
      enableRightArrow: true,
      enableLeftArrow: false,
      showArrows: this.initializeItemsToShow().showArrows,
      itemsToShow: this.initializeItemsToShow().items
    };
  }

  public componentWillMount() {
    window.addEventListener('resize', this.handleWindowSizeChange);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowSizeChange);
  }

  private initializeItemsToShow = (): {
    items: number;
    showArrows: boolean;
  } => {
    const windowWidth = window.innerWidth;
    let currentSize = 100000;
    let items = 0;
    let showArrows = true;
    for (const size in this.props.responsive) {
      if (windowWidth <= Number(size) && currentSize > Number(size)) {
        currentSize = Number(size);
        items = this.props.responsive[size].items;
        showArrows = this.props.responsive[size].showArrows;
      }
    }
    return { items, showArrows };
  };

  private handleWindowSizeChange = (): void => {
    const windowWidth = window.innerWidth;
    let currentSize = 100000;
    for (const size in this.props.responsive) {
      if (windowWidth <= Number(size) && currentSize > Number(size)) {
        currentSize = Number(size);
        this.setState({
          itemsToShow: this.props.responsive[size].items,
          showArrows: this.props.responsive[size].showArrows
        });
      }
    }
  };

  private showRightArrowBasedOnItemsNumber = (): boolean => {
    return this.state.itemsToShow >= React.Children.count(this.props.children)
      ? false
      : true;
  };

  private getItemsNumber = (): number => {
    return this.state.itemsToShow >= React.Children.count(this.props.children)
      ? React.Children.count(this.props.children)
      : this.state.itemsToShow;
  };

  private currentResolution = () => {
    const windowWidth = window.innerWidth;
    const currentSize = 100000;
    for (const size in this.props.responsive) {
      if (windowWidth <= Number(size) && currentSize > Number(size)) {
        return size;
      }
    }
    return currentSize;
  };

  private getFlexBasisOnResolution = () => {
    const items = this.getItemsNumber();
    const size = this.currentResolution();
    if (items === 0) {
      return 100;
    } else if (items === 1) {
      return this.props.responsive[size].singleitemPercentage;
    } else if (items === 2) {
      if (size < 768) {
        return 50;
      }
      return this.props.responsive[size].singleitemPercentage;
    }
  };

  private getSlotWidth = (): number => {
    return this.getItemsNumber() > 2
      ? 100 / this.getItemsNumber()
      : this.getFlexBasisOnResolution();
  };

  private previousSlide = (): void => {
    const lastIndex = React.Children.count(this.props.children) - 1;
    const { currentIndex } = this.state;
    const shouldResetIndex = currentIndex === 0;
    const index = shouldResetIndex ? lastIndex : currentIndex - 1;

    this.setState((prevState: any) => ({
      currentIndex: index,
      translateValue: prevState.translateValue + this.getSlotWidth(),
      enableRightArrow: true,
      enableLeftArrow: index === 0 ? false : true
    }));
  };

  private nextSlide = (): void => {
    const lastIndex = React.Children.count(this.props.children) - 1;
    const { currentIndex } = this.state;
    const shouldResetIndex = currentIndex === lastIndex;
    const index = shouldResetIndex ? 0 : currentIndex + 1;

    this.setState((prevState: any) => ({
      currentIndex: index,
      translateValue: prevState.translateValue + -this.getSlotWidth(),
      enableRightArrow:
        index === lastIndex || index === lastIndex - this.getItemsNumber() + 1
          ? false
          : true,
      enableLeftArrow: true
    }));
  };

  private handleSwipe = throttle(
    (isNext: boolean) => {
      if (isNext) {
        if (
          this.state.enableRightArrow &&
          this.showRightArrowBasedOnItemsNumber()
        ) {
          this.nextSlide();
        }
      } else {
        if (this.state.enableLeftArrow) {
          this.previousSlide();
        }
      }
    },
    500,
    { trailing: false }
  );

  public render() {
    return (
      <Swipeable
        onSwipingLeft={() => this.handleSwipe(true)}
        onSwipingRight={() => this.handleSwipe(false)}
        trackMouse={true}
      >
        <div className="rp-carousel--wrapper">
          {this.state.showArrows && this.state.enableLeftArrow && (
            <Arrow
              iconName="rpds-chevron-left"
              clickFunction={this.previousSlide}
            />
          )}

          <div className="rp-carousel--window">
            <div
              className="rp-carousel--container"
              style={{
                transform: `translate3d(${this.state.translateValue}%, 0, 0)`,
                transition: 'transform ease-in-out 0.45s',
                willChange: 'transform'
              }}
            >
              {React.Children.map(this.props.children, child => (
                <div
                  className={
                    this.getItemsNumber() <= 2 &&
                    (this.state.itemsToShow > 2 || this.getItemsNumber() === 1)
                      ? 'rp-carousel--slot'
                      : 'rp-carousel--slot'
                  }
                  style={{
                    flexBasis: `${this.getSlotWidth()}%`
                  }}
                >
                  {child}
                </div>
              ))}
            </div>
          </div>
          {this.state.showArrows &&
            this.showRightArrowBasedOnItemsNumber() &&
            this.state.enableRightArrow && (
              <Arrow
                iconName="rpds-chevron-right"
                clickFunction={this.nextSlide}
              />
            )}
        </div>
      </Swipeable>
    );
  }
}

export default Carousel;
